import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip, Paper, Tabs, Tab } from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Download as DownloadIcon,
  AccountBalance as BankIcon,
  Verified as VerifiedIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import bankAccountService, {
  BankAccount,
  BankAccountFilters,
  BankAccountStats,
  VerifyBankAccountData,
  ACCOUNT_TYPES,
  VERIFICATION_STATUS,
  IRANIAN_BANKS
} from '../services/bankAccountService';
import './EntityManagement.css';
import AssigneeCell from '../components/common/AssigneeCell';

type TabValue = 'all' | 'pending' | 'verified' | 'rejected';

const BankAccountManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState<TabValue>('all');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<{
    bankAccounts: BankAccount[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  } | null>(null);
  const [stats, setStats] = useState<BankAccountStats | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verificationData, setVerificationData] = useState<VerifyBankAccountData>({
    verificationStatus: 'verified',
    rejectionReason: '',
    adminNotes: ''
  });

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<BankAccountFilters>({
    search: '',
    verificationStatus: 'all',
    bankName: '',
    accountType: 'all'
  });

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { enqueueSnackbar } = useSnackbar();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [accountsData, statsData] = await Promise.all([
        bankAccountService.getAllBankAccounts(page, rowsPerPage, filters),
        bankAccountService.getBankAccountStats()
      ]);
      setData(accountsData.data);
      setStats(statsData.data);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'خطا در بارگذاری اطلاعات', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, enqueueSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    setTabValue(newValue);
    setFilters(prev => ({ ...prev, verificationStatus: newValue }));
    setPage(1);
  };

  const handleFilterChange = (field: keyof BankAccountFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchInput }));
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({ search: '', verificationStatus: tabValue, bankName: '', accountType: 'all' });
    setPage(1);
  };

  const handleViewDetails = async (account: BankAccount) => {
    setSelectedAccount(account);
    setDetailsOpen(true);
    // ✅ لیست شماره حساب/کارت را ماسک می‌کند؛ برای تأیید، نسخهٔ کامل از GET /:id گرفته می‌شود
    try {
      const full = await bankAccountService.getBankAccountById(account._id);
      if (full && (full as any)._id) setSelectedAccount({ ...account, ...(full as any) });
    } catch { /* نمایش نسخهٔ ماسک‌شده */ }
  };

  const handleVerifyClick = (account: BankAccount) => {
    setSelectedAccount(account);
    setVerificationData({
      verificationStatus: 'verified',
      rejectionReason: '',
      adminNotes: ''
    });
    setVerifyDialogOpen(true);
  };

  const handleVerifySubmit = async () => {
    if (!selectedAccount) return;
    try {
      setLoading(true);
      await bankAccountService.verifyBankAccount(selectedAccount._id, verificationData);
      enqueueSnackbar(
        verificationData.verificationStatus === 'verified'
          ? 'حساب بانکی با موفقیت تایید شد'
          : 'حساب بانکی رد شد',
        { variant: 'success' }
      );
      setVerifyDialogOpen(false);
      loadData();
    } catch (error: any) {
      enqueueSnackbar(error.message || 'خطا در پردازش', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async (documentPath: string) => {
    if (!selectedAccount) return;
    try {
      const blob = await bankAccountService.downloadVerificationDocument(
        selectedAccount._id,
        documentPath
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = documentPath.split('/').pop() || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'خطا در دانلود', { variant: 'error' });
    }
  };

  const getStatusTag = (status: string) => {
    const cls = status === 'verified' ? 'tag-success' : status === 'rejected' ? 'tag-danger' : 'tag-warning';
    const Icon = status === 'verified' ? VerifiedIcon : status === 'rejected' ? ErrorIcon : PendingIcon;
    return (
      <span className={`tag tag-sm ${cls}`}>
        <Icon fontSize="inherit" />
        {VERIFICATION_STATUS[status as keyof typeof VERIFICATION_STATUS]}
      </span>
    );
  };

  const totalPages = data?.pagination.totalPages || 1;
  const startItem = data ? (page - 1) * rowsPerPage + 1 : 0;
  const endItem = data ? Math.min(page * rowsPerPage, data.pagination.total) : 0;
  const hasActiveFilters = !!(filters.search || filters.bankName || (filters.accountType && filters.accountType !== 'all'));

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت حساب‌های بانکی</h1>
          <p className="entity-subtitle">تأیید و مدیریت حساب‌های فروشندگان</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={loadData} disabled={loading} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="entity-stats">
          <div className="entity-stat-card">
            <div className="entity-stat-icon blue"><BankIcon /></div>
            <div>
              <p className="entity-stat-label">کل حساب‌ها</p>
              <p className="entity-stat-value">{stats.total || 0}</p>
            </div>
          </div>
          <div className="entity-stat-card">
            <div className="entity-stat-icon orange"><PendingIcon /></div>
            <div>
              <p className="entity-stat-label">در انتظار تایید</p>
              <p className="entity-stat-value">{stats.pending || 0}</p>
            </div>
          </div>
          <div className="entity-stat-card">
            <div className="entity-stat-icon green"><VerifiedIcon /></div>
            <div>
              <p className="entity-stat-label">تایید شده</p>
              <p className="entity-stat-value">{stats.verified || 0}</p>
            </div>
          </div>
          <div className="entity-stat-card">
            <div className="entity-stat-icon red"><ErrorIcon /></div>
            <div>
              <p className="entity-stat-label">رد شده</p>
              <p className="entity-stat-value">{stats.rejected || 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2, borderRadius: '12px' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ px: 2, borderBottom: '1px solid var(--color-border)' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="همه حساب‌ها" value="all" />
          <Tab label="در انتظار" value="pending" icon={<PendingIcon fontSize="small" />} iconPosition="start" />
          <Tab label="تایید شده" value="verified" icon={<VerifiedIcon fontSize="small" />} iconPosition="start" />
          <Tab label="رد شده" value="rejected" icon={<ErrorIcon fontSize="small" />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجو در فروشندگان..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">نوع حساب</label>
            <select
              className="filter-field"
              value={filters.accountType}
              onChange={(e) => handleFilterChange('accountType', e.target.value)}
            >
              <option value="all">همه انواع حساب</option>
              {Object.entries(ACCOUNT_TYPES).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">بانک</label>
            <select
              className="filter-field"
              value={filters.bankName}
              onChange={(e) => handleFilterChange('bankName', e.target.value)}
            >
              <option value="">همه بانک‌ها</option>
              {IRANIAN_BANKS.map((bank) => (
                <option key={bank} value={bank}>{bank}</option>
              ))}
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {filters.search && (
              <span className="filter-tag">
                جستجو: {filters.search}
                <button onClick={() => { setSearchInput(''); setFilters(prev => ({ ...prev, search: '' })); }}>×</button>
              </span>
            )}
            {filters.accountType && filters.accountType !== 'all' && (
              <span className="filter-tag">
                نوع: {ACCOUNT_TYPES[filters.accountType as keyof typeof ACCOUNT_TYPES]}
                <button onClick={() => handleFilterChange('accountType', 'all')}>×</button>
              </span>
            )}
            {filters.bankName && (
              <span className="filter-tag">
                بانک: {filters.bankName}
                <button onClick={() => handleFilterChange('bankName', '')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : !data?.bankAccounts || data.bankAccounts.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><BankIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">هیچ حساب بانکی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>فروشنده</th>
                  <th>بانک</th>
                  <th>نوع حساب</th>
                  <th>شماره حساب</th>
                  <th>شماره شبا</th>
                  <th>وضعیت</th>
                  <th>مسئول</th>
                  <th>تاریخ ایجاد</th>
                  <th style={{ width: 120 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {data.bankAccounts.map((account) => (
                  <tr key={account._id}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-info">
                          <span className="entity-name">
                            {account.sellerId?.name || account.sellerId?.businessName || account.sellerId?.email || 'نامشخص'}
                          </span>
                          <span className="entity-desc">{account.sellerId?.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>{account.bankName}</td>
                    <td>
                      <span className="tag tag-sm tag-inactive">
                        {ACCOUNT_TYPES[account.accountType]}
                      </span>
                    </td>
                    <td><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{account.accountNumber}</span></td>
                    <td><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{account.iban}</span></td>
                    <td>{getStatusTag(account.verificationStatus)}</td>
                    <td><AssigneeCell value={(account as any).assignedTo} /></td>
                    <td>
                      <span className="entity-desc">
                        {new Date(account.createdAt).toLocaleDateString('fa-IR')}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="مشاهده جزئیات" arrow>
                          <button className="action-btn edit" onClick={() => handleViewDetails(account)}>
                            <ViewIcon />
                          </button>
                        </Tooltip>
                        {account.verificationStatus === 'pending' && (
                          <Tooltip title="تایید/رد" arrow>
                            <button className="action-btn toggle active" onClick={() => handleVerifyClick(account)}>
                              <ApproveIcon />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {data.bankAccounts.map((account) => (
                <div key={account._id} className="entity-card">
                  <div className="entity-card-header">
                    <div className="entity-card-image"><BankIcon /></div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">
                        {account.sellerId?.name || account.sellerId?.businessName || account.sellerId?.email || 'نامشخص'}
                      </div>
                      <div className="entity-card-desc">{account.bankName} - {ACCOUNT_TYPES[account.accountType]}</div>
                      <div className="entity-card-desc" style={{ fontFamily: 'monospace' }}>{account.iban}</div>
                    </div>
                    <div className="entity-card-status">
                      {getStatusTag(account.verificationStatus)}
                    </div>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => handleViewDetails(account)}>
                      <ViewIcon fontSize="small" /> جزئیات
                    </button>
                    {account.verificationStatus === 'pending' && (
                      <button className="entity-card-btn" onClick={() => handleVerifyClick(account)}>
                        <ApproveIcon fontSize="small" /> تایید/رد
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {startItem} تا {endItem} از {data.pagination.total}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{page} / {totalPages || 1}</span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        className="bankaccount-details-dialog"
      >
        <div className="edit-dialog-title">
          <span>جزئیات حساب بانکی</span>
          <button className="details-close" onClick={() => setDetailsOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {selectedAccount && (
            <div className="edit-form">
              {/* Store / Seller Info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات فروشنده</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>فروشگاه</label>
                      <input type="text" value={selectedAccount.sellerId?.name || selectedAccount.sellerId?.businessName || 'نامشخص'} readOnly />
                    </div>
                    <div className="edit-field">
                      <label>ایمیل</label>
                      <input type="text" value={selectedAccount.sellerId?.email || '-'} readOnly />
                    </div>
                    {selectedAccount.sellerId?.phoneNumber && (
                      <div className="edit-field">
                        <label>شماره تماس</label>
                        <input type="text" value={selectedAccount.sellerId.phoneNumber} readOnly />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Bank Info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات بانک</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام بانک</label>
                      <input type="text" value={selectedAccount.bankName} readOnly />
                    </div>
                    <div className="edit-field">
                      <label>نوع حساب</label>
                      <input type="text" value={ACCOUNT_TYPES[selectedAccount.accountType]} readOnly />
                    </div>
                    {selectedAccount.branchName && (
                      <div className="edit-field">
                        <label>نام شعبه</label>
                        <input type="text" value={selectedAccount.branchName} readOnly />
                      </div>
                    )}
                    {selectedAccount.branchCode && (
                      <div className="edit-field">
                        <label>کد شعبه</label>
                        <input type="text" value={selectedAccount.branchCode} readOnly />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Account Details */}
              <div className="edit-card">
                <div className="edit-card-header">جزئیات حساب</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام صاحب حساب</label>
                      <input type="text" value={selectedAccount.accountHolderName} readOnly />
                    </div>
                    <div className="edit-field">
                      <label>شماره حساب</label>
                      <input
                        type="text"
                        value={bankAccountService.formatAccountNumber(selectedAccount.accountNumber, false)}
                        readOnly
                        style={{ fontFamily: 'monospace' }}
                      />
                    </div>
                    <div className="edit-field full">
                      <label>شماره شبا (IBAN)</label>
                      <input
                        type="text"
                        value={bankAccountService.formatIban(selectedAccount.iban)}
                        readOnly
                        style={{ fontFamily: 'monospace' }}
                      />
                    </div>
                    {selectedAccount.cardNumber && (
                      <div className="edit-field full">
                        <label>شماره کارت</label>
                        <input
                          type="text"
                          value={bankAccountService.formatCardNumber(selectedAccount.cardNumber, false)}
                          readOnly
                          style={{ fontFamily: 'monospace' }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Verification Status & History */}
              <div className="edit-card">
                <div className="edit-card-header">وضعیت تایید</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>وضعیت</label>
                      <div style={{ paddingTop: 4 }}>{getStatusTag(selectedAccount.verificationStatus)}</div>
                    </div>
                    <div className="edit-field">
                      <label>تاریخ ایجاد</label>
                      <input type="text" value={new Date(selectedAccount.createdAt).toLocaleDateString('fa-IR')} readOnly />
                    </div>
                    {selectedAccount.verifiedAt && (
                      <div className="edit-field">
                        <label>تاریخ تایید</label>
                        <input type="text" value={new Date(selectedAccount.verifiedAt).toLocaleDateString('fa-IR')} readOnly />
                      </div>
                    )}
                    {selectedAccount.rejectionReason && (
                      <div className="edit-field full">
                        <label>دلیل رد</label>
                        <textarea value={selectedAccount.rejectionReason} readOnly rows={2} />
                      </div>
                    )}
                    {selectedAccount.adminNotes && (
                      <div className="edit-field full">
                        <label>یادداشت ادمین</label>
                        <textarea value={selectedAccount.adminNotes} readOnly rows={2} />
                      </div>
                    )}
                  </div>

                  {selectedAccount.verificationDocuments.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 8 }}>
                        مدارک ارسال شده
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {selectedAccount.verificationDocuments.map((doc, index) => (
                          <button
                            key={index}
                            className="entity-card-btn"
                            onClick={() => handleDownloadDocument(doc)}
                            style={{ flex: '0 0 auto' }}
                          >
                            <DownloadIcon fontSize="small" /> مدرک {index + 1}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setDetailsOpen(false)}>بستن</button>
          {selectedAccount?.verificationStatus === 'pending' && (
            <button
              className="edit-btn submit"
              onClick={() => {
                setDetailsOpen(false);
                if (selectedAccount) handleVerifyClick(selectedAccount);
              }}
            >
              تایید/رد
            </button>
          )}
        </div>
      </Dialog>

      {/* Verify Dialog */}
      <Dialog
        open={verifyDialogOpen}
        onClose={() => setVerifyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        className="bankaccount-verify-dialog"
      >
        <div className="edit-dialog-title">
          <span>تایید یا رد حساب بانکی</span>
          <button className="details-close" onClick={() => setVerifyDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">تصمیم</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>تصمیم</label>
                    <select
                      value={verificationData.verificationStatus}
                      onChange={(e) => setVerificationData(prev => ({
                        ...prev,
                        verificationStatus: e.target.value as 'verified' | 'rejected'
                      }))}
                      style={{
                        height: 42,
                        padding: '0 12px',
                        border: '1px solid var(--color-border)',
                        borderRadius: 8,
                        fontSize: '0.875rem',
                        background: 'var(--color-bg-paper)',
                        color: 'var(--color-text-primary)'
                      }}
                    >
                      <option value="verified">تایید</option>
                      <option value="rejected">رد</option>
                    </select>
                  </div>

                  {verificationData.verificationStatus === 'rejected' && (
                    <div className="edit-field full">
                      <label>دلیل رد <span style={{ color: '#dc2626' }}>*</span></label>
                      <textarea
                        value={verificationData.rejectionReason}
                        onChange={(e) => setVerificationData(prev => ({
                          ...prev,
                          rejectionReason: e.target.value
                        }))}
                        placeholder="دلیل رد حساب بانکی را وارد کنید..."
                        rows={3}
                      />
                    </div>
                  )}

                  <div className="edit-field full">
                    <label>یادداشت ادمین (اختیاری)</label>
                    <textarea
                      value={verificationData.adminNotes}
                      onChange={(e) => setVerificationData(prev => ({
                        ...prev,
                        adminNotes: e.target.value
                      }))}
                      placeholder="یادداشت داخلی برای ادمین‌ها..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setVerifyDialogOpen(false)}>انصراف</button>
          <button
            className="edit-btn submit"
            onClick={handleVerifySubmit}
            disabled={
              loading ||
              (verificationData.verificationStatus === 'rejected' && !verificationData.rejectionReason?.trim())
            }
          >
            {loading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {verificationData.verificationStatus === 'verified' ? 'تایید' : 'رد'}
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default BankAccountManagement;
